import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { AbstractSandbox } from 'app/core/sandboxes/abstract.sandbox';
import { ActiveCustomerService } from 'app/core/services/active-customer.service';
import { ContractInfoCharacteristics, ContractInfoService } from 'app/core/services/contract-info.service';
import { CustomerInfoService } from 'app/core/services/customer-info.service';
import { FlowDetail, FlowDetailTO } from 'app/core/store/edit-florder-detail/florder-detail.interface';
// eslint-disable-next-line no-duplicate-imports
import * as actions from 'app/core/store/edit-flow-detail/flow-detail.actions';
import * as flowActions from 'app/core/store/edit-flow-detail/flow-detail.actions';
import * as flowDetailActions from 'app/core/store/edit-flow-detail/flow-detail.actions';
import { FlowDetailCreateStartAction, FlowDetailEditAcceptFlowAction, FlowDetailEditDisputeAction, FlowDetailEditFlowAction } from 'app/core/store/edit-flow-detail/flow-detail.actions';
import { Customer, isCustomer } from 'app/core/store/session/user-context/user-context.interface';
import { firstValueFrom, from, Observable, Subject, switchMap } from 'rxjs';
import { distinctUntilChanged, first, map } from 'rxjs/operators';
import { Florder } from '../interfaces/florder.interface';
import { FlowRestService } from '../services/rest/flow.rest.service';
import { Definitions } from '../store/definitions/definitions.interface';
import * as flowsActions from '../store/flows/flows.actions';
import { goAction } from '../store/router/router.actions';
import { getFirst$$, safe } from '../util/util';

@Injectable({ providedIn: 'root' })
export class FlowSandbox extends AbstractSandbox {
  customersThatCanAcceptFlows$ = this.store.select(state => state.session.customerPermissions.data.acceptFlow);
  customersThatCanUpsertFlows$ = this.store.select(state => state.session.customerPermissions.data.createOrUpdateFlow);

  private customerIds: string[] = [];
  private customers: Customer[] = [];
  private selectedCustomer: Customer;
  private isSuperUser: boolean;
  private definitions: Definitions;

  constructor(
    private flowRestService: FlowRestService,
    private contractInfoService: ContractInfoService,
    private customerInfoService: CustomerInfoService,
    private activeCustomerService: ActiveCustomerService,
  ) {
    super();
    this.store
      .pipe(
        map(state => {
          return {
            isSuperUser: state.session.userContext.isSuperUser,
            selectedCustomer: state.session.selectedCustomer.data,
            customers: state.session.userContext.customers,
            customerInfoCustomer: state.customerInfo.customer,
            allowedCustomers: state.session.customerPermissions.data.queryOrGetFlow,
            definitions: state.definitions,
          };
        }),
        distinctUntilChanged()
      )
      .subscribe(({ isSuperUser, customers, customerInfoCustomer, selectedCustomer, allowedCustomers, definitions }) => {
        this.definitions = definitions;
        this.isSuperUser = isSuperUser;
        this.selectedCustomer = selectedCustomer;
        if (isSuperUser) {
          this.customers = isSuperUser ? allowedCustomers : customers;
          this.customerIds = this.customers.map(c => c.id);
        } else {
          this.customers = [customerInfoCustomer];
          this.customerIds = [];
        }
      });
  }

  async acceptFlow(flow: Florder): Promise<void> {
    await this.activateCustomerForEdit(flow);
    this.store.dispatch(new flowsActions.FlowsAcceptFlowStartAction({ flow }));
  }

  async cancelFlow(flow: Florder): Promise<void> {
    await this.activateCustomerForEdit(flow);
    this.store.dispatch(new flowsActions.FlowsCancelFlowStartAction({ flow }));
  }

  async sendDisputeFlow(flow: FlowDetail): Promise<void> {
    const customerId = flow.customer && flow.customer.id;
    const { result, error } = await safe(getFirst$$(this.flowRestService.sendDisputeFlow(customerId, flow)));

    this.notificationService.showSubmitFeedbackDialog({
      isSuccess: !error,
      messageType: 'DISPUTE',
      route: {
        moduleRoute: 'flows',
        backUrl: 'flows',
      },
      data: {
        etmOrderNumber: '' + flow.etmOrderNumber,
        salesOrderNumber: '' + flow.salesOrderNumber,
      },
    });
  }

  async acceptFlowBatch(flow: Florder): Promise<void> {
    // Change state to 'blacklisted'
    // Do rest call
    // after rest call, do state change to remove from blacklisted
    this.store.dispatch(new flowsActions.FlowsAcceptFlowFromBatchStartAction({ flow }));
    await this.activateCustomerForEdit(flow);

    this.store
      .select('definitions')
      .pipe(first())
      .subscribe((definitions: Definitions) => {
        const statusses = definitions.flow.status;
        this.flowRestService
          .acceptFlow({
            ...flow,
            comments: null, // The comments will be stored as commentsHandshaker.
          })
          .subscribe({
            next: json => {
              json.status = statusses.find(status => status.id === json.status);
              this.store.dispatch(
                new flowsActions.FlowsAcceptFlowFromBatchSuccessAction({
                  flow,
                  status: json.status,
                })
              );
            },
            error: error => {
              this.azureMonitoringService.logException(error, { message: 'flow accept batch fail' });
              this.store.dispatch(new flowsActions.FlowsAcceptFlowFromBatchFailAction({ flow, error }));
            }
          }
          );
      });
  }

  async checkFlowDate(flowDate: string, frequency: 'T' | 'W' | 'D', fromId: string, toId: string, flowWeek) {
    return await firstValueFrom(this.flowRestService.validateFlowDate(flowDate, frequency, fromId, toId, flowWeek));
  }

  loadContractInfoFromFlowTypeToIncoterm(flowTypeId: string, forRegistration: boolean): Observable<void> {
    const subject = new Subject<void>();
    if (!flowTypeId) {
      this.azureMonitoringService.logEvent('loadContractInfoFromOrderTypeToIncoterm from flowSandbox.loadContractInfoFromFlowTypeToIncoterm(): orderType is null. Will fail !');
    }
    this.contractInfoService.loadContractInfoFromOrderTypeToIncoterm(forRegistration ? 'FLOWR' : 'FLOWH', flowTypeId).subscribe(() => {
      subject.complete();
    });
    return subject;
  }

  loadContractInfoMaterials(params: { orderTypeId: string; fromId: string; toId: string }, forRegistration: boolean): Observable<void> {
    return this.contractInfoService.loadContractInfoMaterials({
      ...params,
      shippingCondition: '-',
      incoterm: '-',
      type: forRegistration ? 'FLOWR' : 'FLOWH',
    });
  }

  openFlow(customerId: string, etmOrderNumber: string, salesOrderNumber: string): Observable<boolean> {
    const subject = new Subject<boolean>();
    this.store.dispatch(new flowActions.FlowDetailOpenAction({ etmOrderNumber, salesOrderNumber }));
    this.flowRestService.getFlow(etmOrderNumber, salesOrderNumber, this.customerIds).subscribe(
      result => {
        this.store.pipe(first()).subscribe(state => {
          const customer = this.findCustomer(result.customerNumber);
          this.store.dispatch(
            new flowActions.FlowDetailOpenSuccessAction({
              flowDetail: result,
              contractInfo: state.contractInfo,
              customerInfo: state.customerInfo,
              definitions: state.definitions,
              customer,
            })
          );
          subject.next(true);
          subject.complete();
        });
      },
      error => {
        this.handleFail(error, 'FLOWS.LIST.LABELS.OPEN_FLOW');
        this.azureMonitoringService.logException(error, { message: 'flow open fail' });
        this.store.dispatch(new flowActions.FlowDetailOpenFailAction(error));
        subject.error(error);
      }
    );
    return subject;
  }

  downloadDocument(flow: Florder, customerIds: string[]) {
    this.store.dispatch(
      new flowDetailActions.FlowDetailDocumentDownloadAction({
        etmOrderNumber: flow.etmOrderNumber,
        extension: 'PDF',
        customerIds,
      })
    );
  }

  async copyFlow(customerId: string, etmOrderNumber: string) {
    const subject = new Subject<boolean>();

    this.store.dispatch(new flowActions.FlowDetailCopyFlowAction({ etmOrderNumber }));

    try {
      this.flowRestService.getFlow(etmOrderNumber, '', this.customerIds).subscribe(async flow => {
        const customer = this.findCustomer(customerId);
        await this.activateCustomerForEdit(customer);
        this.contractInfoService.loadContractInfoAndCheck(this.flowTypeTOToContractInfoCharacteristics(flow, true)).subscribe(isValid => {
          if (isValid) {
            this.store.pipe(first()).subscribe(state => {
              this.store.dispatch(
                new flowActions.FlowDetailCopyOpenSuccessAction({
                  flowDetail: flow,
                  contractInfo: state.contractInfo,
                  customerInfo: state.customerInfo,
                  definitions: state.definitions,
                  customer,
                })
              );
              // await this.activeCustomerService.setActiveCustomer(customer);
              this.store.dispatch(goAction({ path: '/flows/copy' }));
              subject.next(true);
              subject.complete();
            });
          } else {
            this.store.dispatch(new flowActions.FlowDetailCopyOpenNoValidContractAction());
            this.handleFail({ messageCode: 'ERRORS.COPY_ORDER.NO_VALID_CONTRACT' }, 'ORDERS.DETAIL.LABELS.COPY_ORDER');
            subject.next(false);
            subject.complete();
          }
        });
      });
    } catch (error) {
      this.handleFail(error, 'ORDERS.DETAIL.LABELS.COPY_ORDER');
      this.azureMonitoringService.logException(error, { message: 'flow copy fail' });
      this.store.dispatch(new flowActions.FlowDetailCopyOpenFailAction(error));
      subject.error(error);
    }
    return subject.asObservable();
  }

  async gotoAcceptFlow(flow: Florder) {
    await this.activateCustomerForEdit(flow);
    this.store.dispatch(goAction({ path: `/flows/${flow.etmOrderNumber}/accept` }));
  }

  async gotoEditFlow(flowData: { flow: Florder; isRegistrar: boolean; isHandshaker: boolean }) {
    await this.activateCustomerForEdit(flowData.flow);
    this.store.dispatch(
      goAction({
        path: [`/flows/${flowData.flow.etmOrderNumber}/edit`],
        query: { isHandshaker: flowData.flow.isHandshaker },
      })
    );
  }

  async gotoEditDispute(flowData: { flow: Florder; isRegistrar: boolean; isHandshaker: boolean }) {
    await this.activateCustomerForEdit(flowData.flow);
    this.store.dispatch(goAction({ path: `/flows/${flowData.flow.etmOrderNumber}/dispute` }));
  }

  /**
   * Registrars can edit their flow.  They need flowRegistrationTypes from contractInfo and can manipulate certain fields and all materials
   * @param etmOrderNumber
   */
  editFlow(customerId: string, etmOrderNumber: string, isHandshaker: boolean = false): Observable<boolean> {
    const actionFn = ({ flow, state, customer }) =>
      new FlowDetailEditFlowAction({
        flowDetail: flow,
        customerInfo: state.customerInfo,
        contractInfo: state.contractInfo,
        definitions: state.definitions,
        customer,
      });
    return this.loadFlowForManipulation(customerId, etmOrderNumber, !isHandshaker, actionFn);
  }

  /**
   * Handshakers can update the flow.  They need flowHandshakeTypes from contractInfo and can manipulate certain fields and all materials (no delete)
   * @param etmOrderNumber
   */
  updateFlow(customerId: string, etmOrderNumber: string): Observable<boolean> {
    const actionFn = ({ flow, state, customer }) =>
      new FlowDetailEditAcceptFlowAction({
        flowDetail: flow,
        customerInfo: state.customerInfo,
        contractInfo: state.contractInfo,
        definitions: state.definitions,
        customer,
      });
    return this.loadFlowForManipulation(customerId, etmOrderNumber, false, actionFn);
  }

  disputeFlow(customerId: string, etmOrderNumber: string, isHandshaker: boolean = false): Observable<boolean> {
    const actionFn = ({ flow, state, customer }) =>
      new FlowDetailEditDisputeAction({
        flowDetail: flow,
        customerInfo: state.customerInfo,
        contractInfo: state.contractInfo,
        definitions: state.definitions,
        customer,
      });
    return this.loadFlowForManipulation(customerId, etmOrderNumber, !isHandshaker, actionFn);
  }

  /**
   * Handshakers can update the flow.  They need flowHandshakeTypes from contractInfo and can manipulate certain fields and all materials (no delete)
   * @param customerId
   * @param etmOrderNumber
   * @param successActionFn
   */
  loadFlowForManipulation(customerId: string, etmOrderNumber: string, forRegistration: boolean, successActionFn: ({ flow, state, customer }) => Action): Observable<boolean> {
    const subject = new Subject<boolean>();
    this.flowRestService.getFlow(etmOrderNumber, '').subscribe(async flow => {
      const customer = this.findCustomer(customerId);
      await this.activateCustomerForEdit(customer);
      this.contractInfoService.loadContractInfoAndCheck(this.flowTypeTOToContractInfoCharacteristics(flow, forRegistration)).subscribe(isValid => {
        if (isValid) {
          setTimeout(() => {
            this.store.pipe(first()).subscribe(state => {
              this.store.dispatch(successActionFn({ flow, state, customer }));
              subject.next(true);
              subject.complete();
            });
          }, 10); // Just wait a moment so that action is reduced
        } else {
          this.handleFail({ messageCode: 'ERRORS.FLOW.EDIT.CONTRACT_CHANGED' }, 'ERRORS.FLOW.EDIT.TITLE');
          subject.next(false);
          subject.complete();
        }
      });
    });
    return subject.asObservable();
  }

  resetCustomerInfo(): void {
    this.customerInfoService.resetCustomerInfo();
  }

  loadCustomerInfo(customer: Customer, forceLoad = false): Observable<void> {
    return from(this.activeCustomerService.setActiveCustomer(customer)).pipe(
      switchMap(() => this.customerInfoService.loadCustomerInfo(customer, forceLoad))
    );
  }

  async getSelectedCustomer(): Promise<Customer> {
    return await this.activeCustomerService.getSelectedCustomer();
  }

  async getCustomerForEdit(): Promise<Customer> {
    if (this.isSuperUser) {
      return await this.activeCustomerService.getActiveCustomer();
    } else {
      return await this.activeCustomerService.getSelectedCustomer();
    }
  }

  async activateCustomerForEdit(flowOrIdOrCustomer: Florder | string | Customer) {
    await new Promise<void>(async (resolve) => {
      if (this.isSuperUser) {
        let customer;
        if (isCustomer(flowOrIdOrCustomer)) {
          customer = flowOrIdOrCustomer;
        } else if (typeof flowOrIdOrCustomer === 'string') {
          customer = this.customers.find(cust => cust.id === flowOrIdOrCustomer);
        } else {
          customer = this.customers.find(cust => cust.id === flowOrIdOrCustomer.customerNumber);
        }
        if (customer) {
          await this.activeCustomerService.setActiveCustomer(customer);
          this.customerInfoService.loadCustomerInfo(customer, true).subscribe(resolve);
        }
      } else {
        resolve();
      }
    });
  }

  showWarning(titleCode: string, messageCode: string) {
    this.notificationService.showModal({
      disableClose: false,
      titleCode,
      messageCode,
      subMessages: [],
      type: 'error',
    });
  }

  resetCreatePage() {
    if (!this.isSuperUser) {
      this.store.dispatch(goAction({ path: '/flows' }));
    } else {
      this.store.dispatch(
        new FlowDetailCreateStartAction({
          selectedCustomer: this.selectedCustomer,
          isSuperUser: this.isSuperUser,
        })
      );
    }
  }

  async showEmptyContractModal() {
    await this.notificationService.showModal({
      type: 'error',
      titleCode: 'WARNING',
      messageCode: 'FLOWS.NOTIFICATIONS.EMPTY_CONTRACT',
      buttonText: 'CLOSE',
      disableClose: false,
    });
    this.resetCreatePage();
  }

  showQuantityExceedsTreshholdModal() {
    this.notificationService.showModal({
      type: 'warning',
      titleCode: 'WARNING',
      messageCode: 'FLOWS.NOTIFICATIONS.MAX_AMOUNT_ITEMS',
      buttonText: 'CLOSE',
      disableClose: false,
    });
    this.store.dispatch(new flowsActions.FlowsMaxAmountPopupShownAction());
  }

  resetMaxAmountPopup() {
    this.store.dispatch(new flowsActions.FlowsMaxAmountPopupResetAction());
  }

  private findCustomer(customerId: string): Customer {
    return this.customers.find(cust => cust.id === customerId);
  }

  private flowTypeTOToContractInfoCharacteristics(flowDetailTO: FlowDetailTO, forRegistration: boolean): ContractInfoCharacteristics {
    return {
      type: forRegistration ? 'FLOWR' : 'FLOWH',
      orderType: flowDetailTO.flowType,
      shippingCondition: '-',
      fromId: flowDetailTO.from.id,
      toId: flowDetailTO.to.id,
      incoterm: '-',
    };
  }

  private flowDetailToContractInfoCharacteristics(flowDetail: FlowDetail, forRegistration: boolean): ContractInfoCharacteristics {
    return {
      type: forRegistration ? 'FLOWR' : 'FLOWH',
      orderType: flowDetail.deliveryMethod.type,
      shippingCondition: '-',
      fromId: flowDetail.deliveryMethod.from.id,
      toId: flowDetail.deliveryMethod.to.id,
      incoterm: '-',
    };
  }

  sortMaterials(sortField: string, sortAscending: boolean) {
    this.store.dispatch(
      new flowDetailActions.FlowDetailSortMaterialsAction({
        sortField,
        sortAscending,
        definitions: this.definitions,
      })
    );
  }

  async submitCreateOrUpdate(data: { flowDetailForm: any }): Promise<boolean> {
    this.store.dispatch(new actions.FlowDetailCreateOrUpdateSubmitAction(data));

    const editFlowDetail = await getFirst$$(this.store.select(state => state.editFlowDetail));
    const customerId = editFlowDetail.flowDetail.customer && editFlowDetail.flowDetail.customer.id;
    let apiCall: Observable<any>;
    // Hier wordt bepaald of het een create of een update is
    if (editFlowDetail.mode.state === 'edit-confirm') {
      apiCall = this.flowRestService.updateFlow(customerId, editFlowDetail.flowDetail);
    } else {
      apiCall = this.flowRestService.createFlow(customerId, editFlowDetail.flowDetail);
    }

    const { error, result } = await safe(getFirst$$(apiCall));
    if (error) {
      await this.handleFail(error, 'Submit flow');
      this.store.dispatch(new actions.FlowDetailCreateOrUpdateSubmitFailAction());
      return false;
    }
    this.store.dispatch(new actions.FlowDetailCreateOrUpdateSubmitSuccessAction({ result }));
    return true;
  }
}
